import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getNetlifyFunctionsBase } from "../utils/netlifyFunctions.js";
import { ACTIONS_ROUTE } from "../constants/routes.js";
import { getActionCategoryMeta, inferActionCategory } from "../constants/actionCategories.js";
import ActionCategoryIcon from "./ActionCategoryIcon.js";

/**
 * Compact open-action card above Quick Clarity for signed-in users.
 * Never blocks a new reflection.
 */
const OpenActionBanner = ({ verifiedEmail }) => {
  const [action, setAction] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!verifiedEmail) {
      setAction(null);
      return;
    }
    try {
      const response = await fetch(`${getNetlifyFunctionsBase()}/.netlify/functions/getUserActions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: verifiedEmail })
      });
      if (!response.ok) {
        setAction(null);
        return;
      }
      const data = await response.json();
      const open = Array.isArray(data.open) ? data.open : [];
      setAction(open[0] || null);
    } catch (_) {
      setAction(null);
    }
  }, [verifiedEmail]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!verifiedEmail) {
        if (!cancelled) setAction(null);
        return;
      }
      try {
        const response = await fetch(`${getNetlifyFunctionsBase()}/.netlify/functions/getUserActions`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: verifiedEmail })
        });
        if (!response.ok) {
          if (!cancelled) setAction(null);
          return;
        }
        const data = await response.json();
        const open = Array.isArray(data.open) ? data.open : [];
        if (!cancelled) setAction(open[0] || null);
      } catch (_) {
        if (!cancelled) setAction(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [verifiedEmail]);

  const markDone = async () => {
    if (!action?.insightId || !verifiedEmail || busy) return;
    setBusy(true);
    try {
      const response = await fetch(`${getNetlifyFunctionsBase()}/.netlify/functions/updateInsightUserAction`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          insightId: action.insightId,
          email: verifiedEmail,
          status: "done"
        })
      });
      if (response.ok) {
        setAction(null);
        await load();
      }
    } catch (err) {
      console.error("Unable to mark action done", err);
    } finally {
      setBusy(false);
    }
  };

  if (!verifiedEmail || !action) return null;

  const category = action.category || inferActionCategory(action.text);
  const categoryMeta = getActionCategoryMeta(category);
  const dueLabel = action.due_at
    ? new Date(action.due_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })
    : null;

  return (
    <div className="open-action-banner" role="status">
      <div className="open-action-banner__icon-wrap" aria-label={categoryMeta.label}>
        <ActionCategoryIcon category={category} />
      </div>
      <div className="open-action-banner__body">
        {dueLabel ? (
          <div className="open-action-banner__top">
            <span className="open-action-banner__due">Due {dueLabel}</span>
          </div>
        ) : null}
        <p className="open-action-banner__text">{action.text}</p>
        <Link className="open-action-banner__review" to={ACTIONS_ROUTE}>
          Review all
        </Link>
      </div>
      <button
        type="button"
        className="open-action-banner__done"
        onClick={markDone}
        disabled={busy}
      >
        {busy ? "Saving…" : "Done"}
      </button>
    </div>
  );
};

export default OpenActionBanner;
